import type { InstatiateEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// The generated GraphQL list-scalar variables widen to `string | string[]`;
// the tRPC procedure takes plain arrays, so the wrapper works with this shape.
export interface InstantiateEnterpriseRiskInput {
  EnterpriseRiskIds: string[];
  Entities: string[];
}

// Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape (V2
// reported `affected_rows`). Note the legacy GraphQL op name is misspelled
// (`instatiate…`); the new hook name is spelled correctly.
const mapTrpcResponseToGraphQL = (
  affectedRows: number
): InstatiateEnterpriseRiskMutation => ({
  instantiateChildEnterpriseRisk: {
    __typename: 'GenericMutationOutput',
    affected_rows: affectedRows,
  },
});

/**
 * tRPC implementation of the instantiate-enterprise-risk mutation (RSP-6498).
 * NOT imported outside this directory — consumers use the flag-gated
 * `useInstantiateEnterpriseRisk` wrapper. Instantiation creates risk trees, so
 * both the risk and enterprise-risk read surfaces are invalidated on success.
 */
export const useInstantiateEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.instantiate.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.riskById.queryKey(),
          }),
          // Instantiation creates new descendant risks, so the
          // remove-from-enterprise-risk warning (useGetRiskDescendantIds) must
          // refresh too, matching the register/by-id invalidations.
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.descendantIds.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    instantiateEnterpriseRisk: async (
      variables: InstantiateEnterpriseRiskInput
    ): Promise<InstatiateEnterpriseRiskMutation> => {
      const result = await mutation.mutateAsync({
        EnterpriseRiskIds: variables.EnterpriseRiskIds,
        Entities: variables.Entities,
      });

      return mapTrpcResponseToGraphQL(result.affectedRows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
